<template>
  <div v-if="!hasPermission('List Transfer Table')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar />
    <div class="container-xxl">
      <div class="card">
        <div class="card-header">
          <div class="card-title">
            <h3 class="fw-bold">Tabelas de repasse</h3>
            <p class="text-muted mb-0">
              Para cada local é criada automaticamente a tabela &quot;Convênio Padrão&quot;, onde ficam os convênios do local por padrão.
              Cadastre outras tabelas de convênio quando necessário e distribua os convênios entre elas.
            </p>
          </div>
          <div class="card-toolbar">
            <button
              type="button"
              class="btn btn-primary"
              v-if="hasPermission('Store Transfer Table')"
              data-bs-toggle="modal"
              data-bs-target="#modal_store_transfer_table"
              @click="resetFormStore"
            >
              <i class="bi bi-plus-lg"></i> Nova tabela
            </button>
          </div>
        </div>
        <div class="card-body py-4">
          <div v-if="loading" class="text-center py-10">Carregando...</div>
          <table v-else class="table table-row-bordered gy-5">
            <thead>
              <tr class="fw-bold fs-6 text-muted">
                <th>Nome</th>
                <th>Ativa</th>
                <th>Convênios/Planos</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tables" :key="row.id_transfer_table">
                <td>{{ row.transfer_table_name }}</td>
                <td>{{ row.transfer_table_active ? 'Sim' : 'Não' }}</td>
                <td>
                  <span class="text-gray-800">{{ linkedInsuranceCount(row.id_transfer_table) }} vinculado(s)</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-primary ms-2"
                    v-if="hasPermission('List Place Insurance')"
                    @click="openManageInsurances(row)"
                    title="Ver ou alterar convênios/planos desta tabela"
                  >
                    <i class="bi bi-building"></i>
                    {{ canEditInsuranceLinks ? 'Gerenciar' : 'Visualizar' }}
                  </button>
                </td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-icon btn-bg-light btn-active-color-primary me-1"
                    v-if="hasPermission('Update Transfer Table')"
                    data-bs-toggle="modal"
                    data-bs-target="#modal_edit_transfer_table"
                    @click="openEdit(row)"
                    title="Editar"
                  >
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-icon btn-bg-light btn-active-color-primary"
                    v-if="hasPermission('Delete Transfer Table') && row.transfer_table_name !== 'Convênio Padrão'"
                    @click="confirmDelete(row)"
                    title="Excluir"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="modal fade" tabindex="-1" id="modal_store_transfer_table" ref="modalStore">
      <div class="modal-dialog">
        <form @submit.prevent="submitStore">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Nova tabela de repasse</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
            </div>
            <div class="modal-body">
              <div class="mb-4">
                <label class="form-label required">Nome</label>
                <input type="text" class="form-control" v-model="formStore.transfer_table_name" maxlength="100" required />
              </div>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="store_active" v-model="formStore.transfer_table_active" />
                <label class="form-check-label" for="store_active">Ativa</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="modal fade" tabindex="-1" id="modal_edit_transfer_table" ref="modalEdit">
      <div class="modal-dialog">
        <form @submit.prevent="submitEdit">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Editar tabela de repasse</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
            </div>
            <div class="modal-body">
              <div class="mb-4">
                <label class="form-label required">Nome</label>
                <input type="text" class="form-control" v-model="formEdit.transfer_table_name" maxlength="100" required />
              </div>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="edit_active"
                  :checked="formEdit.transfer_table_active"
                  @change="onEditActiveChange"
                />
                <label class="form-check-label" for="edit_active">Ativa</label>
              </div>
              <p
                v-if="formEdit.id_transfer_table && linkedInsuranceCount(formEdit.id_transfer_table) > 0"
                class="text-muted small mb-0 mt-2"
              >
                Só é possível desativar quando não houver convênios ou planos vinculados a esta tabela.
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="modal fade" tabindex="-1" id="modal_manage_insurances" ref="modalManageInsurances">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Convênios/Planos — {{ manageTable?.transfer_table_name }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-4">
              Marque os convênios/planos que devem usar <strong>esta</strong> tabela de repasse. Cada convênio fica em uma tabela por vez; o vínculo
              <strong>não pode ser desfeito</strong> aqui — para mudar de tabela, abra a outra tabela e marque o convênio nela.
            </p>
            <div v-if="insurancesLoading" class="text-center py-5">Carregando convênios…</div>
            <div v-else-if="!insurances.length" class="alert alert-warning">
              Nenhum convênio/plano cadastrado para este local.
              <router-link to="/insurances" class="alert-link">Cadastrar convênios</router-link>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-row-bordered align-middle">
                <thead>
                  <tr class="text-muted fw-semibold">
                    <th style="width: 52px" class="text-center">Vínculo</th>
                    <th>Convênio/Plano</th>
                    <th>Apelido</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ins in insurances" :key="ins.id_insurance">
                    <td class="text-center">
                      <input
                        v-if="canEditInsuranceLinks"
                        type="checkbox"
                        class="form-check-input"
                        :disabled="insuranceVinculoLocked(ins)"
                        :checked="!!manageSelected[ins.id_insurance]"
                        :title="
                          insuranceVinculoLocked(ins)
                            ? 'Vínculo não pode ser removido. Para usar outra tabela, abra essa tabela e marque o convênio/plano.'
                            : ''
                        "
                        @change="toggleInsurance(ins.id_insurance, $event.target.checked)"
                      />
                      <i
                        v-else
                        class="bi"
                        :class="manageSelected[ins.id_insurance] ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'"
                      ></i>
                    </td>
                    <td>{{ ins.code_ans }} — {{ ins.insurance_name }}</td>
                    <td>{{ ins.place_insurance?.nick_name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
            <button
              type="button"
              class="btn btn-primary"
              v-if="canEditInsuranceLinks && insurances.length"
              :disabled="savingLinks"
              @click="saveInsuranceLinks"
            >
              Salvar vínculos
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import Permission from "../../helpers/permission";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";

export default {
  name: "TransferTables",
  components: { Toolbar },
  data() {
    return {
      loading: false,
      saving: false,
      tables: [],
      insurances: [],
      insurancesLoading: false,
      manageTable: null,
      /** @type {Record<number, boolean>} */
      manageSelected: {},
      /** @type {Record<number, boolean>} */
      manageInitial: {},
      savingLinks: false,
      formStore: {
        transfer_table_name: "",
        transfer_table_active: true,
      },
      formEdit: {
        id_transfer_table: null,
        transfer_table_name: "",
        transfer_table_active: true,
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    canEditInsuranceLinks() {
      return Permission.has("Update Place Insurance");
    },
  },
  mounted() {
    this.loadTables();
    this.loadInsurances();
  },
  methods: {
    hasPermission(p) {
      return Permission.has(p);
    },
    loadTables() {
      this.loading = true;
      http
        .get(`/places/${this.place.id_place}/transfer-tables`)
        .then((res) => {
          this.tables = res.data.data || [];
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message || "Erro ao carregar tabelas.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInsurances() {
      this.insurancesLoading = true;
      return http
        .get(`/places/${this.place.id_place}/insurances`)
        .then((res) => {
          this.insurances = res.data || [];
        })
        .catch(() => {
          this.insurances = [];
        })
        .finally(() => {
          this.insurancesLoading = false;
        });
    },
    linkedInsuranceCount(transferTableId) {
      return this.insurances.filter(
        (i) => i.place_insurance && Number(i.place_insurance.id_transfer_table) === Number(transferTableId)
      ).length;
    },
    async openManageInsurances(row) {
      this.manageTable = row;
      await this.loadInsurances();
      const sel = {};
      const initial = {};
      const tid = Number(row.id_transfer_table);
      this.insurances.forEach((ins) => {
        const on = ins.place_insurance && Number(ins.place_insurance.id_transfer_table) === tid;
        sel[ins.id_insurance] = on;
        initial[ins.id_insurance] = on;
      });
      this.manageSelected = { ...sel };
      this.manageInitial = { ...initial };
      const el = document.getElementById("modal_manage_insurances");
      if (el && window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(el).show();
      }
    },
    /**
     * Convênio já estava vinculado a esta tabela ao abrir o modal — não permite desmarcar.
     */
    insuranceVinculoLocked(ins) {
      return !!this.manageInitial[ins.id_insurance];
    },
    toggleInsurance(idInsurance, checked) {
      if (this.manageInitial[idInsurance] && !checked) {
        return;
      }
      this.manageSelected = {
        ...this.manageSelected,
        [idInsurance]: checked,
      };
    },
    examCategoriesPayload(insurance) {
      const raw = insurance.place_insurance?.exam_categories || [];
      return raw.map((c) => ({
        id_spec_group: c.id_spec_group,
        name: c.name,
        required: !!c.required,
      }));
    },
    buildInsurancePutPayload(insurance, idTransferTable) {
      const pi = insurance.place_insurance || {};
      const nickName =
        pi.nick_name != null && String(pi.nick_name).trim() !== ""
          ? String(pi.nick_name).trim().slice(0, 30)
          : String(insurance.insurance_name || insurance.code_ans || "Convênio/Plano").trim().slice(0, 30) ||
            "Convênio/Plano";
      let value = 0;
      if (pi.value != null && pi.value !== "") {
        const n = Number(pi.value);
        value = Number.isFinite(n) ? n : 0;
      }
      const payload = {
        id_insurance: insurance.id_insurance,
        observation: pi.observation ?? "",
        nick_name: nickName,
        value,
        id_transfer_table: idTransferTable,
        exam_categories: this.examCategoriesPayload(insurance),
      };
      if (pi.available_to_patient !== undefined && pi.available_to_patient !== null) {
        payload.available_to_patient = !!pi.available_to_patient;
      }
      return payload;
    },
    async saveInsuranceLinks() {
      if (!this.manageTable) {
        return;
      }
      const targetId = this.manageTable.id_transfer_table;
      const toUpdate = [];
      this.insurances.forEach((ins) => {
        const id = ins.id_insurance;
        const was = !!this.manageInitial[id];
        const now = !!this.manageSelected[id];
        if (was === now) {
          return;
        }
        if (was && !now) {
          return;
        }
        if (!was && now) {
          toUpdate.push({ insurance: ins, id_transfer_table: targetId });
        }
      });
      if (!toUpdate.length) {
        Alert.toastSuccess("Nenhuma alteração.");
        const el = document.getElementById("modal_manage_insurances");
        if (el && window.bootstrap?.Modal) {
          window.bootstrap.Modal.getInstance(el)?.hide();
        }
        return;
      }
      this.savingLinks = true;
      try {
        for (const item of toUpdate) {
          const payload = this.buildInsurancePutPayload(item.insurance, item.id_transfer_table);
          await http.put(
            `/places/${this.place.id_place}/insurances/${item.insurance.id_insurance}`,
            payload
          );
          item.insurance.place_insurance = {
            ...item.insurance.place_insurance,
            id_transfer_table: item.id_transfer_table,
          };
        }
        Alert.toastSuccess("Vínculos atualizados.");
        await this.loadInsurances();
        const el = document.getElementById("modal_manage_insurances");
        if (el && window.bootstrap?.Modal) {
          window.bootstrap.Modal.getInstance(el)?.hide();
        }
      } catch (e) {
        Alert.toastError(e.response?.data?.message || e.response?.data || "Erro ao salvar vínculos.");
      } finally {
        this.savingLinks = false;
      }
    },
    resetFormStore() {
      this.formStore = {
        transfer_table_name: "",
        transfer_table_active: true,
      };
    },
    submitStore() {
      this.saving = true;
      http
        .post(`/places/${this.place.id_place}/transfer-tables`, {
          transfer_table_name: this.formStore.transfer_table_name,
          transfer_table_active: this.formStore.transfer_table_active,
        })
        .then(() => {
          Alert.toastSuccess("Tabela cadastrada.");
          this.loadTables();
          const el = document.getElementById("modal_store_transfer_table");
          if (el && window.bootstrap?.Modal) {
            window.bootstrap.Modal.getInstance(el)?.hide();
          }
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message || "Erro ao salvar.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openEdit(row) {
      this.formEdit = {
        id_transfer_table: row.id_transfer_table,
        transfer_table_name: row.transfer_table_name,
        transfer_table_active: row.transfer_table_active,
      };
    },
    onEditActiveChange(e) {
      const next = e.target.checked;
      if (!next && this.linkedInsuranceCount(this.formEdit.id_transfer_table) > 0) {
        e.target.checked = true;
        Alert.toastError(
          "Não é possível desativar a tabela enquanto houver convênios ou planos vinculados.",
        );
        return;
      }
      this.formEdit.transfer_table_active = next;
    },
    submitEdit() {
      this.saving = true;
      http
        .put(`/places/${this.place.id_place}/transfer-tables/${this.formEdit.id_transfer_table}`, {
          transfer_table_name: this.formEdit.transfer_table_name,
          transfer_table_active: this.formEdit.transfer_table_active,
        })
        .then(() => {
          Alert.toastSuccess("Tabela atualizada.");
          this.loadTables();
          const el = document.getElementById("modal_edit_transfer_table");
          if (el && window.bootstrap?.Modal) {
            window.bootstrap.Modal.getInstance(el)?.hide();
          }
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message || "Erro ao salvar.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(row) {
      if (!confirm(`Excluir a tabela "${row.transfer_table_name}"?`)) {
        return;
      }
      http
        .delete(`/places/${this.place.id_place}/transfer-tables/${row.id_transfer_table}`)
        .then(() => {
          Alert.toastSuccess("Tabela excluída.");
          this.loadTables();
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message || e.response?.data || "Não foi possível excluir.");
        });
    },
  },
};
</script>
